/**
 * MSML HEALTH - Analysis Domain Model & Adapter
 * Decouples UI from backend API response shapes.
 */

export const DIMENSION_CONFIG = {
  agency:               { label: 'Agency',               color: '#2E79FF', description: 'Empowers user decision-making' },
  clarity:              { label: 'Clarity',              color: '#10B981', description: 'Clear and unambiguous communication' },
  emotional_regulation: { label: 'Emotional Regulation', color: '#EC4899', description: 'Supports emotional wellbeing' },
  cognitive_load:       { label: 'Cognitive Load',       color: '#F5A623', description: 'Minimizes mental effort required' },
  safety:               { label: 'Safety & Risk',        color: '#8B5CF6', description: 'Avoids potential harms' },
  dependency:           { label: 'Dependency Risk',      color: '#EF4444', description: 'Fosters independence, not reliance' },
};

export const DOMAIN_OPTIONS = [
  { value: '', label: 'Auto-detect' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'education', label: 'Education' },
  { value: 'finance', label: 'Finance' },
  { value: 'legal', label: 'Legal' },
  { value: 'customer_service', label: 'Customer Service' },
  { value: 'mental_health', label: 'Mental Health' },
  { value: 'general', label: 'General' },
];

export const AUDIENCE_OPTIONS = [
  { value: '', label: 'Auto-detect' },
  { value: 'general', label: 'General Public' },
  { value: 'professional', label: 'Professional' },
  { value: 'vulnerable', label: 'Vulnerable' },
  { value: 'children', label: 'Children' },
  { value: 'elderly', label: 'Elderly' },
];

export const SENSITIVITY_OPTIONS = [
  { value: '', label: 'Auto-detect' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

/** Adapt raw API response into a clean domain model */
export function adaptAnalysis(raw) {
  if (!raw) return null;

  const dimensionScoresRaw = raw.dimension_scores || {};

  const dimensions = Object.entries(dimensionScoresRaw).map(([key, score]) => ({
    key,
    score,
    ...(DIMENSION_CONFIG[key] || { label: key, color: '#2E79FF', description: '' }),
    drivers: (raw.drivers || []).filter(d => d.dimension === key),
  }));

  const drivers = (raw.drivers || []).map(d => ({
    dimension: d.dimension,
    dimensionLabel: DIMENSION_CONFIG[d.dimension]?.label || d.dimension,
    reason: d.reason,
    impact: d.impact,
    color: DIMENSION_CONFIG[d.dimension]?.color || '#2E79FF',
  }));

  const evidence = (raw.evidence || []).map(e => ({
    quote: e.quote || e.quote_text || '',
    dimension: e.dimension || e.dimension_name || 'unknown',
    dimensionLabel: DIMENSION_CONFIG[e.dimension || e.dimension_name]?.label || e.dimension || e.dimension_name,
    type: e.type || e.span_type || 'neutral',
    impact: e.impact || 0,
    color: DIMENSION_CONFIG[e.dimension || e.dimension_name]?.color || '#71717A',
  }));

  const optimization = raw.optimized_text
    ? {
        text: raw.optimized_text,
        improvements: (raw.improvements || []).map(i => ({
          dimension: i.dimension,
          dimensionLabel: DIMENSION_CONFIG[i.dimension]?.label || i.dimension,
          change: i.change,
          delta: i.expected_delta || 0,
          color: DIMENSION_CONFIG[i.dimension]?.color || '#2E79FF',
        })),
        deltas: (raw.deltas || []).map(d => ({
          dimension: d.dimension,
          original: d.original,
          optimized: d.optimized,
          delta: d.delta,
        })),
        beforeScores: raw.before_scores || null,
        afterScores: raw.after_scores || null,
        deltaOverall: raw.delta_overall || 0,
        deltaDimensions: raw.delta_dimensions || {},
        blocked: raw.blocked || false,
        blockReason: raw.block_reason || null,
      }
    : null;

  const judge = raw.judge_verdict
    ? {
        verdict: raw.judge_verdict.overall_verdict,
        approved: raw.judge_verdict.approved,
        intentStatus: raw.judge_verdict.intent_status,
        intentSimilarity: raw.judge_verdict.intent_similarity,
        safetyStatus: raw.judge_verdict.safety_status,
        dependencyIncrease: raw.judge_verdict.dependency_increase,
        notes: raw.judge_verdict.notes,
      }
    : null;

  return {
    id: raw.analysis_id,
    originalText: raw.original_text,
    hqScore: raw.hq_score,
    hqScoreRaw: raw.hq_score_raw,
    storageMode: raw.storage_mode,
    rubricVersion: raw.rubric_version,
    createdAt: raw.created_at,
    dimensions,
    drivers,
    evidence,
    flags: raw.flags || {},
    capsApplied: raw.caps_applied || [],
    signals: raw.signals,
    determinism: raw.determinism || null,
    optimization,
    judge,
  };
}

export function getScoreGrade(score) {
  if (score >= 85) return { label: 'Excellent', color: '#10B981', cssClass: 'msml-score-excellent' };
  if (score >= 70) return { label: 'Good', color: '#4C8DFF', cssClass: 'msml-score-good' };
  if (score >= 50) return { label: 'Acceptable', color: '#F5A623', cssClass: 'msml-score-acceptable' };
  return { label: 'Needs Improvement', color: '#EF4444', cssClass: 'msml-score-poor' };
}

export function getDimensionScoreColor(score) {
  if (score >= 70) return '#10B981';
  if (score >= 50) return '#F5A623';
  return '#EF4444';
}
